import { useLocale } from "../hooks/useLocale";
import { msg } from "../locales/translate";
import { useEffect, useMemo, useState } from "react";
import { DateRangePicker } from "./conponents/DateRangePicker";
import Button from "@ui/button/Button";
import { HBox } from "@ui/box/Box";
import { CreditCard, FileJson, Landmark, Mail, RotateCw } from "lucide-react";
import styled from "styled-components";
import type { IPaymentSession } from "src/types/types";
import { BankAnalysisDetails } from "./statView/BankAnalysisDetails";
import { BankAnalysisError, loadBankAnalysis, type EntityAnalysisRequest } from "./statView/overviewBankAnalysis";
import { parseBankAnalysis } from "./statView/bankDeclineData";
import { analysisPeriodLabel } from "./statView/analysisPeriodLabel";
import { AnalysisPeriodCaption } from "./statView/AnalysisPeriodCaption";
import { transactionAnalysisPeriod, transactionAnalysisTargets, validAnalysisPeriod } from "./transactionAnalyticsData";
import { S_RAW_TEXT_POPUP } from "./conponents/popup/RawTextPopup";
import { useTranslation } from "react-i18next";
import { getAppLanguage } from "src/i18n";
import { EntityBanAction } from "./bans/EntityBanAction";

const AnalyticsLayout = styled.div`
	display: flex; flex-direction: column; gap: 12px; min-width: 0; container-type: inline-size;
`;
const Toolbar = styled.div`
	display: grid; grid-template-columns: auto minmax(200px, 1fr) auto; align-items: center; gap: 12px;
	@container (max-width: 820px) {
		grid-template-columns: auto minmax(0, 1fr);
		form { grid-column: 1 / -1; justify-self: end; }
	}
	@container (max-width: 480px) {
		grid-template-columns: minmax(0, 1fr);
	}
	@container (max-width: 380px) { form { width: 100%; } }
`;
const ObjectToggle = styled.div`
	display: inline-flex; align-items: center; justify-self: start; gap: 3px; padding: 3px;
	border: 1px solid #e0e5ed; border-radius: 9px; background: #e9edf3;
	button {
		display: inline-flex; align-items: center; justify-content: center; gap: 6px;
		height: 30px; padding: 0 10px; border: 0; border-radius: 6px;
		background: transparent; color: #667085; font: inherit; font-size: 12px; font-weight: 500;
		cursor: pointer; white-space: nowrap; transition: background .15s, color .15s, box-shadow .15s;
	}
	button:hover { color: #344054; background: #ffffff80; }
	button[aria-pressed="true"] { color: #263847; background: #fff; box-shadow: 0 1px 4px #24334b1f; }
	button:focus-visible { outline: 2px solid #6374d9; outline-offset: -2px; }
`;
const PeriodCaption = styled.div`
	display: flex; align-items: center; gap: 6px; min-width: 0;
	color: #667085; font-size: 11px; line-height: 1.5;
	> span { min-width: 0; }
	> svg { flex-shrink: 0; }
`;
const Filters = styled.form`
	display: flex; align-items: center; justify-content: flex-end; gap: 8px; margin: 0; min-width: 0;
	> button:focus-visible { outline: 2px solid #6374d9; outline-offset: -2px; }
	> button {
		height: 34px; padding: 0 12px; border: 0; border-radius: 8px;
		background: #101b25; color: #fff; font: inherit; font-size: 12px; cursor: pointer; flex-shrink: 0;
	}
	> button:hover { background: #263847; }
	@container (max-width: 380px) { gap: 5px; > button { padding: 0 8px; } }
`;

const EntityDetails = ({ request }: { request: EntityAnalysisRequest }) => {
	 useLocale();
	const { t } = useTranslation();
	const [state, setState] = useState<{ busy: boolean; raw?: string; error?: { message: string; bankAnalysis: boolean } }>({ busy: true });
	const [attempt, setAttempt] = useState(0);
	const analysis = useMemo(() => state.raw && !state.error ? parseBankAnalysis(state.raw, request) : null, [state.raw, state.error, request]);
	useEffect(() => {
		let cancelled = false;
		setState({ busy: true });
		void loadBankAnalysis(request, attempt > 0).then(
			raw => { if (!cancelled) setState({ busy: false, raw }); },
			error => { if (!cancelled) setState({ busy: false, error: { message: error instanceof BankAnalysisError ? error.message : t("transactionAnalytics.loadError"), bankAnalysis: error instanceof BankAnalysisError }, raw: error instanceof BankAnalysisError ? error.raw : undefined }); },
		);
		return () => { cancelled = true; };
	}, [request.bank.type, request.bank.value, request.from, request.to, request.currency, request.userId, request.projectId, attempt]);
	const errorMessage = state.error?.bankAnalysis && getAppLanguage() === "ru" ? state.error.message : t("transactionAnalytics.loadError");
	return <section aria-label={t("transactionAnalytics.analysisFor", { name: request.bank.label })} style={{ background: "white", border: "1px solid #e1e6ed", borderRadius: 10, padding: 16, minWidth: 0 }}>
		<HBox gap="12px" sx={{ alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", marginBottom: 16 }}>
			<strong style={{ fontSize: 14, overflowWrap: "anywhere", minWidth: 0, flex: "1 1 180px" }}>{request.bank.label}</strong>
			<HBox gap="8px" sx={{ alignItems: "center", flexShrink: 0 }}>
				<EntityBanAction type={request.bank.type} value={request.bank.value} projectId={request.projectId} userId={request.userId} />
				<Button title={msg("raw")} variant="secondarySmall" icon={<FileJson size={14} aria-hidden="true" />} disabled={state.raw === undefined} onClick={() => {
					if (state.raw === undefined) return;
					S_RAW_TEXT_POPUP.invoke({ title: msg("templateRaw_008c1d84", { v0: request.bank.label }), value: state.raw, summary: [
						{ title: t("transactionAnalytics.periodField"), value: analysisPeriodLabel(request.from, request.to, new Date(), getAppLanguage()) },
						{ title: t("transactionAnalytics.currencyField"), value: request.currency || t("bankAnalysis.allCurrencies") },
					] });
				}} />
				<Button title={state.error ? t("common.retry") : t("common.refresh")} variant="secondarySmall" icon={<RotateCw size={14} aria-hidden="true" />} disabled={state.busy} onClick={() => setAttempt(value => value + 1)} />
			</HBox>
		</HBox>
		<div role="status" aria-live="polite" aria-busy={state.busy}>
			{state.busy && <p style={{ fontSize: 13 }}>{t("transactionAnalytics.loadingAnalysis")}</p>}
			{state.error && <p style={{ color: "#b42332", fontSize: 13 }}>{errorMessage}</p>}
		</div>
		{analysis && <BankAnalysisDetails key={attempt} data={analysis} />}
		{state.raw !== undefined && !analysis && !state.error && <p style={{ color: "#667085", fontSize: 13, lineHeight: 1.5 }}>{t("transactionAnalytics.prepareError")}</p>}
	</section>;
};

export const TransactionAnalytics = ({ session, projectId, userId, ready }: { session: IPaymentSession; projectId?: number; userId?: number; ready: boolean }) => {
	useLocale();
	const { t } = useTranslation();
	const initialPeriod = transactionAnalysisPeriod(session.created_at);
	const [draft, setDraft] = useState(initialPeriod);
	const [period, setPeriod] = useState(initialPeriod);
	const [validation, setValidation] = useState("");
	const [selectedTarget, setSelectedTarget] = useState(() => transactionAnalysisTargets(session).find(target => target.entity)?.kind ?? "email");
	const targets = transactionAnalysisTargets(session);
	const target = targets.find(item => item.kind === selectedTarget) ?? targets[0];
	const targetEntity = target.entity ? { ...target.entity, label: target.labelKey ? t(`transactionAnalytics.${target.labelKey}`) : target.entity.label } : undefined;
	const currency = /^[A-Za-z]{3}$/.test(session.currency || "") ? session.currency.toUpperCase() : "";
	if (!ready) return <p role="status">{t("transactionAnalytics.loadingTransaction")}</p>;
	if (!projectId || !userId || session.project_id && session.project_id !== projectId) return <p>{t("transactionAnalytics.wrongProject")}</p>;
	return <AnalyticsLayout>
		<Toolbar>
			<ObjectToggle role="group" aria-label={t("transactionAnalytics.analysisObject")}>
				{targets.map(item => <button key={item.kind} type="button" aria-pressed={target.kind === item.kind} onClick={() => setSelectedTarget(item.kind)}>
					{item.kind === "email" ? <Mail size={14} aria-hidden="true" /> : item.kind === "card" ? <CreditCard size={14} aria-hidden="true" /> : <Landmark size={14} aria-hidden="true" />}
					{t(`transactionAnalytics.targets.${item.kind}`)}
				</button>)}
			</ObjectToggle>
			<PeriodCaption>
				<AnalysisPeriodCaption from={period.from} to={period.to} currency={currency} />
			</PeriodCaption>
			<Filters aria-label={t("transactionAnalytics.period")} noValidate onSubmit={event => {
				event.preventDefault();
				if (!validAnalysisPeriod(draft.from, draft.to)) { setValidation(t("transactionAnalytics.invalidPeriod")); return; }
				setValidation(""); setPeriod({ ...draft });
			}}>
				<DateRangePicker style={{ flex: "0 1 270px", minWidth: 0 }} from={draft.from} to={draft.to} invalid={!!validation}
					onChange={(from, to) => setDraft(value => ({ ...value, from, to }))} />
				<button type="submit">{t("transactionAnalytics.show")}</button>
			</Filters>
		</Toolbar>
		{validation && <p role="alert" style={{ margin: 0, color: "#b42332", fontSize: 13 }}>{validation}</p>}
		{targetEntity ? <EntityDetails
			key={JSON.stringify([projectId, userId, session.id, targetEntity.type, targetEntity.value, period.from, period.to, currency])}
			request={{ bank: targetEntity, ...period, currency, projectId, userId }}
		/> : <p style={{ margin: 0, color: "#667085", fontSize: 13 }}>{t(`transactionAnalytics.missing.${target.kind}`)}</p>}
	</AnalyticsLayout>;
};
